// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useRef } from 'react'
import type { FC } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { GitCompare } from 'lucide-react'
import type { NodeAdminViewProps } from '../../../../core/types'

type Operator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq'

interface DecisionCondition {
  datasetKey: string
  fieldPath: string
  operator: Operator
  value: number | string | boolean
}

interface DecisionGateSettings {
  condition?: DecisionCondition
}

const OPERATOR_LABELS: Record<Operator, string> = {
  gt: '> (greater than)',
  gte: '>= (greater than or equal)',
  lt: '< (less than)',
  lte: '<= (less than or equal)',
  eq: '= (equal)',
}

export const DecisionGateAdminView: FC<NodeAdminViewProps> = ({
  config,
  onChange,
}) => {
  const settings = config as DecisionGateSettings | undefined

  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const [datasetKey, setDatasetKey] = useState(settings?.condition?.datasetKey ?? '')
  const [fieldPath, setFieldPath] = useState(settings?.condition?.fieldPath ?? '')
  const [operator, setOperator] = useState<Operator>(settings?.condition?.operator ?? 'gt')
  const [value, setValue] = useState(String(settings?.condition?.value ?? ''))
  // Sync condition to parent
  useEffect(() => {
    if (datasetKey && fieldPath && value !== '') {
      const numericValue = Number(value)
      const parsedValue = Number.isNaN(numericValue) ? value : numericValue
      onChangeRef.current?.('condition', {
        datasetKey,
        fieldPath,
        operator,
        value: parsedValue,
      })
    }
  }, [datasetKey, fieldPath, operator, value])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitCompare className="h-5 w-5" />
          Decision Gate
        </CardTitle>
        <CardDescription>
          Filter records based on a condition evaluated against upstream analysis results.
          Records that fail are skipped by downstream nodes.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Condition */}
        <div className="space-y-4">
          <Label className="text-base">Condition</Label>

          <div className="space-y-2">
            <Label htmlFor="dataset-key">Dataset Key</Label>
            <p className="text-xs text-muted-foreground">
              The upstream analysis dataset to evaluate (e.g., score_federal_bill_summary)
            </p>
            <Input
              id="dataset-key"
              value={datasetKey}
              onChange={(e) => setDatasetKey(e.target.value)}
              placeholder="score_federal_bill_summary"
              className="max-w-md"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="field-path">Field Path</Label>
            <p className="text-xs text-muted-foreground">
              Dot-notation path to the field in the analysis output (e.g., report_overview.filter_score.score)
            </p>
            <Input
              id="field-path"
              value={fieldPath}
              onChange={(e) => setFieldPath(e.target.value)}
              placeholder="report_overview.filter_score.score"
              className="max-w-md"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="operator">Operator</Label>
              <Select value={operator} onValueChange={(v) => setOperator(v as Operator)}>
                <SelectTrigger id="operator">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(OPERATOR_LABELS) as [Operator, string][]).map(([op, label]) => (
                    <SelectItem key={op} value={op}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="threshold-value">Value</Label>
              <Input
                id="threshold-value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="3"
              />
            </div>
          </div>
        </div>

      </CardContent>
    </Card>
  )
}
